"use client";

import Image from "next/image";
import { Search, X } from "lucide-react";
import Button from "../common/Button";
import { useState } from "react";
import type Project from "@/types/project";

type ProjectSearchPanelProps = {
    projects: Project[];
    onProjectSelect: (projectId: string) => void;
    setSearchDropdownOpen: (open: boolean) => void;
};

export default function ProjectSearchPanel({
    projects,
    onProjectSelect,
    setSearchDropdownOpen,
}: ProjectSearchPanelProps) {
    const [query, setQuery] = useState("");
    const normalizedQuery = query.trim().toLowerCase();
    const filteredProjects = projects.filter((project) => {
        if (!normalizedQuery) return true;
        return (
            project.title.toLowerCase().includes(normalizedQuery) ||
            project.fileName.toLowerCase().includes(normalizedQuery) ||
            project.description.toLowerCase().includes(normalizedQuery)
        );
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <label className="relative block w-full">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-white/50" />
                    <input
                        type="text"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Rechercher un projet..."
                        className="h-10 w-full rounded-lg border border-white/15 bg-white/5 pl-9 pr-3 text-sm text-white outline-none transition-colors placeholder:text-white/45 focus:border-primary/60"
                    />
                </label>
                <Button
                    variant="outline"
                    size="icon"
                    className="size-10 border-white/15 !rounded-lg shrink-0"
                    onClick={() => setSearchDropdownOpen(false)}
                >
                    <X />
                </Button>
            </div>

            <div className="h-[calc(100vh-198px)] md:max-h-80 space-y-3 overflow-y-auto custom-scrollbar">
                {filteredProjects.map((project) => {
                    return (
                        <button
                            key={project.id}
                            type="button"
                            onClick={() => {
                                onProjectSelect(project.id);
                                setSearchDropdownOpen(false);
                            }}
                            className="w-full rounded-xl border border-white/10 bg-white/5 p-3 text-left transition-colors hover:border-primary/35 hover:bg-white/10"
                        >
                            <div className="flex items-start gap-3">
                                <Image
                                    src={project.image}
                                    alt={project.title}
                                    width={96}
                                    height={56}
                                    sizes="96px"
                                    className="h-14 w-24 rounded-md border border-white/10 object-cover"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="truncate text-sm font-semibold text-white">{project.title}</p>
                                    </div>
                                    <p className="mt-0.5 text-[11px] font-mono text-white/65">{project.fileName}</p>
                                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-white/70">{project.description}</p>
                                </div>
                            </div>
                        </button>
                    );
                })}

                {filteredProjects.length === 0 ? (
                    <div className="rounded-lg border border-white/10 bg-white/5 p-4 text-center text-xs font-mono text-white/60">
                        Aucun projet trouve.
                    </div>
                ) : null}
            </div>
        </div>
    );
}
